import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import type { Channel } from '../types'
import Badge from './ui/Badge'
import { ChannelAvatar } from './ChannelAvatar'

interface Props {
  channels: Channel[]
  activeChannelId?: string | null
}

export default function ChannelSwitcher({ channels, activeChannelId }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const active = channels.find((c) => c.id === activeChannelId)

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  function selectChannel(c: Channel) {
    setOpen(false)
    const isLinks = location.pathname.endsWith('/links')
    navigate(isLinks ? `/channels/${c.id}/links` : `/channels/${c.id}`)
  }

  function bindKick() {
    setOpen(false)
    const token = localStorage.getItem('auth.token')
    if (!token) return
    const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8080'
    window.location.href = `${apiBase}/api/auth/kick/start?token=${encodeURIComponent(token)}`
  }

  if (channels.length === 0) {
    return (
      <button
        onClick={bindKick}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)] border border-dashed border-[var(--color-border)]"
      >
        + Kanal bağla
      </button>
    )
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-sm bg-[var(--color-elevated)] border border-[var(--color-border)] hover:border-[var(--color-border-strong)]"
      >
        <div className="flex items-center gap-2 min-w-0">
          {(active || channels[0]) && (
            <ChannelAvatar channel={active || channels[0]} size={20} />
          )}
          <span className="truncate text-[var(--color-text)]">
            {active?.slug || 'Kanal seç'}
          </span>
        </div>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--color-text-muted)] shrink-0">
          <polyline points="7 9 12 4 17 9" />
          <polyline points="17 15 12 20 7 15" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg overflow-hidden">
          <div className="max-h-72 overflow-y-auto py-1">
            {channels.map((c) => (
              <button
                key={c.id}
                onClick={() => selectChannel(c)}
                className={[
                  'w-full flex items-center gap-2 px-2.5 py-1.5 text-sm text-left',
                  c.id === activeChannelId
                    ? 'bg-[var(--color-elevated)] text-[var(--color-text)]'
                    : 'text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]',
                ].join(' ')}
              >
                <ChannelAvatar channel={c} size={20} />
                <span className="flex-1 truncate">{c.slug}</span>
                <Badge tone={c.is_owner ? 'accent' : 'neutral'}>{c.role}</Badge>
                {c.id === activeChannelId && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-[var(--color-accent)] shrink-0">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-[var(--color-border)]">
            <button
              onClick={bindKick}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-sm text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
            >
              <span className="w-5 h-5 rounded border border-dashed border-[var(--color-border)] flex items-center justify-center text-xs">
                +
              </span>
              Yeni kanal bağla
            </button>
            {channels.length > 1 && (
              <button
                onClick={() => {
                  setOpen(false)
                  navigate('/dashboard?stay=1')
                }}
                className="w-full flex items-center gap-2 px-2.5 py-2 text-xs text-[var(--color-text-subtle)] hover:bg-[var(--color-elevated)] border-t border-[var(--color-border)]"
              >
                Tüm kanalları göster →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
